import styled from '@emotion/styled';

import { useCallback } from 'react';

import COLOR from '../../../../../shared/constant/colorset';

import { addComma } from '../../../../../shared/services';
import { getFormattedDate } from '../../../shared/utils/productDetailState';

import { LegacyPromotionType, MemberCoupon } from '../../../types';

import { Question21x21cccc } from '../../../../../shared/images';
import PriceInformationAlert from '../../../shared/alert/PriceInformationAlert';
import { PriceService } from '../../../../service/priceService';
import { amplitudeService } from '../../../../../shared/amplitude';
import {
  SelectPriceTooltipButton,
} from '../../../../../shared/amplitude/events/product/SelectPriceTooltipButton';
import { getFusionQueryId } from '../../../shared/utils/productDetailEvent';
import { useAppSelector } from '../../../../../shared/store';

const PriceWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  padding-top: 19px;
  font-weight: bold;
  line-height: 30px;
  letter-spacing: -0.5px;
`;

const DiscountPercent = styled.span`
  padding-right: 9px;
  font-size: 28px;
  color: ${COLOR.pointText};
`;

const PriceValue = styled.span`
  padding-right: 4px;
  font-size: 28px;
  color: ${COLOR.kurlyGray800};
`;

const PriceSubText = styled.span`
  display: inline-block;
  position: relative;
  top: 3px;
  font-size: 18px;
  color: ${COLOR.kurlyGray800};
  vertical-align: top;
`;

const SubPriceWrapper = styled.div`
  padding-top: 9px;
`;

const SubPriceText = styled.span<{ lineThrough: boolean; }>`
  font-size: 16px;
  color: ${COLOR.kurlyGray400};
  text-decoration: ${({ lineThrough }) => lineThrough ? 'line-through' : 'none'};
  margin-right: ${({ lineThrough }) => lineThrough ? 0 : '4px'};
  letter-spacing: -0.5px;
`;

const QuestionIcon = styled.img`
  width: 21px;
  height: 21px;
  vertical-align: -4px;
  padding-left: 1px;
  cursor: pointer;
`;

const MemberCouponGuide = styled.div`
  color: ${COLOR.kurlyPurple};
  line-height: 1.43;
  white-space: pre-line;
  margin-top: 10px;
`;

interface Props {
  discountRate: number;
  discountedPrice: number | null;
  basePrice: number;
  retailPrice: number | null;
  isMultiplePrice: boolean;
  legacyPromotion: LegacyPromotionType;
  memberCoupon: MemberCoupon;
}

export default function Price({
  discountRate,
  discountedPrice,
  basePrice,
  retailPrice,
  isMultiplePrice,
  legacyPromotion,
  memberCoupon,
}: Props) {
  const { queryId } = useAppSelector(({ productList }) => productList);

  const {
    representativePrice, kurlyPrice, originalPrice,
  } = new PriceService({ retailPrice, basePrice, discountedPrice });

  const { newbieLimitDatetime, newbieMinPrice } = memberCoupon;

  const lineThrough = !!kurlyPrice && !originalPrice;

  const handleClickPriceInformation = useCallback(() => {
    PriceInformationAlert({
      discountRate, representativePrice, kurlyPrice, originalPrice,
    });

    amplitudeService.logEvent(new SelectPriceTooltipButton({
      fusionQueryId: getFusionQueryId(queryId),
    }));
  }, [discountRate, kurlyPrice, originalPrice, representativePrice]);

  return (
    <>
      <PriceWrapper>
        {discountRate > 0 && (
          <DiscountPercent>
            {discountRate}%
          </DiscountPercent>
        )}
        <PriceValue>
          {addComma(representativePrice)}
        </PriceValue>
        <PriceSubText>
          원{isMultiplePrice && '~'}
        </PriceSubText>
      </PriceWrapper>
      {(kurlyPrice || originalPrice) && (
        <SubPriceWrapper>
          {kurlyPrice && (
            <SubPriceText
              lineThrough={lineThrough}
              onClick={handleClickPriceInformation}
            >
              {addComma(kurlyPrice)}원
              {lineThrough &&
              <QuestionIcon src={Question21x21cccc} alt="컬리판매가 자세히 보기"/>}
            </SubPriceText>
          )}
          {originalPrice && (
            <SubPriceText
              lineThrough
              onClick={handleClickPriceInformation}
            >
              {addComma(originalPrice)}원
              <QuestionIcon src={Question21x21cccc} alt="컬리판매가 자세히 보기"/>
            </SubPriceText>
          )}
        </SubPriceWrapper>
      )}
      {legacyPromotion === 'NEWBIE' && newbieLimitDatetime && (
        <MemberCouponGuide>
          {getFormattedDate(newbieLimitDatetime, 'MM월 dd일 HH시 mm분')}까지 구매 가능{'\n'}
          {addComma(newbieMinPrice)}원 이상 결제 시 구매 가능
        </MemberCouponGuide>
      )}
    </>
  );
}
